import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ModalData, ModalRef, ModalService } from '@purespectrum1/ui/modal';
import { ToasterService } from '@purespectrum1/ui/toaster-service';

import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { IncrementalTitleGenerator } from '../../shared/utils/incremental-title-generator';
import {
  NestedQuota,
  QuotaPayload,
} from '../../shared/interfaces/quota.interface';
import { notifyMessage } from '../../shared/constants/notify-message';

import { QuotaNestingAlert } from './domain/quota-nesting-alerts/quota-nesting-alert-manager';
import { DefaultNestingGroupIdUpdater } from '../domain/survey-nested-quotas-updater';
import {
  EachNestingGroupData,
  MultiQuotaNestingData,
  MultiQuotaNestingReturn,
} from './interfaces/multi-quota-nesting.interface';
import { MultiQuotaNestingService } from './services/multi-quota-nesting.service';
import { NestingGroupDataBuilder } from './domain/nesting-group-data-builder';
import { SurveyNestedQuotas } from '../domain/survey-nested-quotas';
import { NestedQuotaDuplicityFinder } from './domain/nested-quota-duplicity-finder';

@Component({
  selector: 'ps-ui-multi-quota-nesting-modal',
  templateUrl: './multi-quota-nesting-modal.component.html',
  styleUrls: ['./multi-quota-nesting-modal.component.css'],
  providers: [MultiQuotaNestingService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiQuotaNestingModalComponent implements OnInit, OnDestroy {
  private _subscriptions = new Subscription();
  private _nestingGroupData: EachNestingGroupData[] = [];

  private _createAnotherNestingCommand$ =
    new BehaviorSubject<EachNestingGroupData>(<EachNestingGroupData>{});
  private _saveChangesCommand$ = new Subject<void>();
  private _discardChangesCommand$ = new Subject<void>();

  public selectedNestingGroup$!: Observable<EachNestingGroupData>;
  public alerts$ = this._multiQuotaNestingService.getAlerts$();

  public originalQuotas: QuotaPayload[] = this._modalData.quotas;
  public surveyAllocation: number = this._modalData.completes;
  public buyerCompanyId = this._modalData.surveyDetails?.buyer_company;
  public hasUnsavedChanges: boolean = false;
  public pendingNestingGroup: EachNestingGroupData | undefined = undefined;

  constructor(
    @Inject(ModalData)
    private readonly _modalData: MultiQuotaNestingData,
    private readonly _modalRef: ModalRef<
      MultiQuotaNestingModalComponent,
      MultiQuotaNestingReturn
    >,
    private _modal: ModalService,
    private _multiQuotaNestingService: MultiQuotaNestingService,
    private _toaster: ToasterService
  ) {}

  public ngOnInit(): void {
    this._initializeNestingData();
    this._handleNestingGroupStreams();
    this._handlAnotherNestingCreation();
    this._handleUnsavedChanges();
    this._handleNestedDataSyncing();
    this._handleEmptySelectionStateCreation();
    this._handleDiscardChanges();
    this._handleSaveChanges();
    this._handleMoreQuotasThanCompletesAlert();
  }

  private get _surveyNestedQuotas(): SurveyNestedQuotas {
    return new SurveyNestedQuotas(this._modalData.nestedQuotas);
  }

  private _initializeNestingData(): void {
    // If there are already nested quotas in the survey, while initializing data, we need to load all data and select last nesting group selected
    if (this._surveyNestedQuotas.get().length) {
      this._assignDefaultGroupIdToNestedQuotasWithoutGroup();
      this._segregateAndPrepareDataForNestingGroups();
      return;
    }
    this._addNewNestingGroupWithEmptyState();
  }

  private _handleNestingGroupStreams(): void {
    this.selectedNestingGroup$ = this._multiQuotaNestingService
      .getSelectedNestingGroup()
      .pipe(shareReplay(1));
  }

  private _handlAnotherNestingCreation(): void {
    this._subscriptions.add(
      this._createAnotherNestingCommand$
        .pipe(
          filter((selectedNestingGroup) => {
            if (!selectedNestingGroup || !selectedNestingGroup.id) {
              return false;
            }

            if (selectedNestingGroup.state === 'created') {
              return false;
            }

            if (!selectedNestingGroup.selectedQualifications.length) {
              return false;
            }
            return true;
          }),
          switchMap((selectedNestingGroup) =>
            this._validateNestingGroupBeforeCreation(selectedNestingGroup)
          )
        )
        .subscribe((selectedNestingGroup) => {
          const updatedSelectedNestingGroup: EachNestingGroupData = {
            ...selectedNestingGroup,
            state: 'created',
            qualificationSelection: false,
          };
          this._multiQuotaNestingService.setNestingGroupData(
            updatedSelectedNestingGroup.id,
            updatedSelectedNestingGroup
          );
          this._multiQuotaNestingService.sendEmptySelectionStateCreationCommand();
        })
    );
  }

  private _validateCreatedNestingGroupsForQuotaDuplicity() {
    const createdNestingGroups = this._findCreatedNestingGroups();

    const allNestedQuotas = createdNestingGroups
      .map((group) => group.quotas)
      .flat();

    for (const currentNestingGroup of createdNestingGroups) {
      const otherNestedQuotas = allNestedQuotas.filter(
        (quota) => quota.group_key !== currentNestingGroup.id
      );

      const matching = new NestedQuotaDuplicityFinder(
        otherNestedQuotas,
        currentNestingGroup.quotas
      ).matching();

      if (matching.length) {
        this._toaster.error(
          notifyMessage.errorMessage.MULTI_QUOTA_NESTING.DUPLICATE_QUOTAS_BETWEEN_NESTINGS.replace(
            '<NESTING_1>',
            currentNestingGroup.id
          ).replace('<NESTING_2>', matching[0].group_key!)
        );
        return false;
      }
    }

    return true;
  }

  private _validateNestingGroupBeforeCreation(
    upcomingNestingGroup: EachNestingGroupData
  ): Observable<EachNestingGroupData> {
    return of(upcomingNestingGroup).pipe(
      filter((nestingGroup) => {
        const matching = new NestedQuotaDuplicityFinder(
          this._surveyNestedQuotas.get(),
          nestingGroup.quotas
        ).matching();
        if (matching.length) {
          this._toaster.error(
            notifyMessage.errorMessage.MULTI_QUOTA_NESTING.DUPLICATE_QUOTAS_IN_NEW_NESTING.replace(
              '<MATCHED_NESTING>',
              matching[0].group_key!
            )
          );
          return false;
        }
        return true;
      }),
      map(() => upcomingNestingGroup)
    );
  }

  private _validateQuotaAllocation() {
    const hasQuotaAllocationExceeded = this._nestingGroupData.some(
      ({ quotas }) =>
        new SurveyNestedQuotas(quotas).allocation() > this.surveyAllocation
    );

    if (hasQuotaAllocationExceeded) {
      this._toaster.error(
        notifyMessage.errorMessage.MULTI_QUOTA_NESTING.INVALID_QUOTA_COMPLETE
      );
      return false;
    }

    return true;
  }

  private _handleUnsavedChanges(): void {
    this._subscriptions.add(
      this._multiQuotaNestingService
        .hasUnsavedChanges()
        .subscribe((hasUnsavedChanges) => {
          this.hasUnsavedChanges = hasUnsavedChanges;
        })
    );
  }

  private _handleNestedDataSyncing(): void {
    this._subscriptions.add(
      this._multiQuotaNestingService
        .getNestingGroupData()
        .pipe(tap((data) => (this._nestingGroupData = data)))
        .subscribe((data) => {
          this.pendingNestingGroup = this._findPendingNestingGroup();
          this._markLastNestingGroupQualificationSelection();
          this._modalData.nestedQuotas = this._findCreatedNestingGroups()
            .map((each) => each.quotas)
            .flat();
        })
    );
  }

  private _handleEmptySelectionStateCreation(): void {
    this._subscriptions.add(
      this._multiQuotaNestingService
        .getEmptySelectionStateCreationCommand()
        .subscribe(() => {
          this._addNewNestingGroupWithEmptyState();
        })
    );
  }

  private _handleDiscardChanges(): void {
    this._subscriptions.add(
      this._discardChangesCommand$
        .pipe(
          switchMap(() => {
            if (this.hasUnsavedChanges) {
              return this._openCancelConfirmationModal();
            }

            return of('ok');
          }),
          filter((status) => status === 'ok')
        )
        .subscribe(() => this._close(true))
    );
  }

  private _handleSaveChanges(): void {
    this._subscriptions.add(
      this._saveChangesCommand$
        .pipe(
          filter(
            () =>
              this._validateCreatedNestingGroupsForQuotaDuplicity() &&
              this._validateQuotaAllocation()
          ),
          switchMap(() =>
            this.pendingNestingGroup
              ? this._validateNestingGroupBeforeCreation(
                  this.pendingNestingGroup
                )
              : of(null)
          )
        )
        .subscribe(() => {
          if (this.pendingNestingGroup) {
            const nestingGroupToCreate: EachNestingGroupData = {
              ...this.pendingNestingGroup,
              state: 'created',
              qualificationSelection: false,
            };
            this._multiQuotaNestingService.setNestingGroupData(
              nestingGroupToCreate.id,
              nestingGroupToCreate
            );
          }
          this._close(false, this._modalData.nestedQuotas);
        })
    );
  }

  private _markLastNestingGroupQualificationSelection(): void {
    if (this.pendingNestingGroup) {
      this._updateLastNestingGroupQualificationSelection(false);
      return;
    }

    this._updateLastNestingGroupQualificationSelection(true);
  }

  private _updateLastNestingGroupQualificationSelection(selection: boolean) {
    const createdNestingGroups = this._findCreatedNestingGroups();
    const lastNesingGroup = this._findLastItem(createdNestingGroups);
    // If there is no last item or the last item is already marked qual selectable, then no need to proceed
    if (
      !lastNesingGroup ||
      lastNesingGroup.qualificationSelection === selection
    ) {
      return;
    }
    const updatedLastNestingGroup: EachNestingGroupData = {
      ...lastNesingGroup,
      qualificationSelection: selection,
    };
    this._multiQuotaNestingService.setNestingGroupData(
      updatedLastNestingGroup.id,
      updatedLastNestingGroup
    );
  }

  private _assignDefaultGroupIdToNestedQuotasWithoutGroup() {
    this._modalData.nestedQuotas = new DefaultNestingGroupIdUpdater(
      this._surveyNestedQuotas.get()
    ).update();
  }

  private _segregateAndPrepareDataForNestingGroups() {
    for (const groupId of this._surveyNestedQuotas.groupIds()) {
      this._setNestingGroupDataForEachGroup(groupId);
    }
  }

  private _setNestingGroupDataForEachGroup(groupId: string) {
    const nestingGroupData = new NestingGroupDataBuilder(
      this._surveyNestedQuotas.kind(groupId).get(),
      this._modalData.qualifications,
      this._modalData.quotas,
      this.surveyAllocation,
      groupId
    ).build();
    this._multiQuotaNestingService.setNestingGroupData(
      groupId,
      nestingGroupData
    );

    const isLastGroup =
      this._findLastItem(this._surveyNestedQuotas.groupIds()) === groupId;
    if (isLastGroup) {
      this._multiQuotaNestingService.setSelectedNestingGroup(nestingGroupData);
    }
  }

  private _findLastItem<T>(items: T[]): T {
    return items[items.length - 1];
  }

  private _generateNestingGroupId(): string {
    return new IncrementalTitleGenerator(
      this._surveyNestedQuotas.groupIds()
    ).generateWith('Nested');
  }

  public addNewNestingGroupWithEmptyState() {
    this._multiQuotaNestingService.sendEmptySelectionStateCreationCommand();
  }

  private _addNewNestingGroupWithEmptyState() {
    const newGroupId = this._generateNestingGroupId();
    const nestingGroupData = new NestingGroupDataBuilder(
      [],
      this._modalData.qualifications,
      this._modalData.quotas,
      this.surveyAllocation,
      newGroupId
    ).build();
    this._multiQuotaNestingService.setNestingGroupData(
      newGroupId,
      nestingGroupData
    );
    this._multiQuotaNestingService.setSelectedNestingGroup(nestingGroupData);
  }

  private _findPendingNestingGroup(): EachNestingGroupData | undefined {
    return this._nestingGroupData.find((group) => group.state === 'pending');
  }

  private _findCreatedNestingGroups(): EachNestingGroupData[] {
    return this._nestingGroupData.filter((group) => group.state === 'created');
  }

  private _openCancelConfirmationModal(): Observable<string> {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: "You haven't confirmed the Quota Nested created. Do you want to exit without applying the Quota Nested?",
        size: 'md',
      },
    });
    return modalRef.onClose$;
  }

  private _handleMoreQuotasThanCompletesAlert(): void {
    const hasMoreQuotasInAnyNesting = this._surveyNestedQuotas
      .groupIds()
      .some(
        (groupId) =>
          this._surveyNestedQuotas.kind(groupId).get().length >
          this.surveyAllocation
      );
    this._multiQuotaNestingService.setAlertVisibility(
      QuotaNestingAlert.MORE_QUOTAS_THAN_COMPLETES,
      hasMoreQuotasInAnyNesting
    );
  }

  private _close(canceled = false, quotas: NestedQuota[] = []): void {
    this._modalRef.close({ canceled, quotas });
  }

  public createAnotherNesting(
    selectedNestingGroup: EachNestingGroupData
  ): void {
    this._createAnotherNestingCommand$.next(selectedNestingGroup);
    this._multiQuotaNestingService.indicateUnsavedChanges();
  }

  public discardChanges(): void {
    this._discardChangesCommand$.next();
  }

  public saveChanges() {
    this._saveChangesCommand$.next();
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._multiQuotaNestingService.resetData();
  }
}
